import React__default, { useRef, useState, useEffect } from 'react';
import { AkChevronDownIcon } from '@fuegokit/fuegoicons-react';
import InnerVisualSlot from './InnerVisualSlot.js';
import SelectClearButton from './SelectClearButton.js';
import { StyledSelectText, AtlasSelectItem } from './AtlasSelectItem.js';
import 'react-stately';
import { useFilter } from 'react-aria';
import '@react-aria/ssr';
import { StyledSelect, StyledSelectLabel, StyledSelectInputWrapper, UnstyledSelectInput, StyledSelectTrigger, StyledSelectPopover, StyledSelectListbox } from './StyledSelect.js';
import Box from '../Box/Box.js';
import Stack from '../Stack/Stack.js';
import Text from '../Text/Text.js';

/* eslint-disable @typescript-eslint/no-explicit-any */
const AtlasComboboxSelect = ({
  children,
  description,
  errorMessage,
  defaultFilter,
  placeholder,
  leadingVisual: LeadingVisual,
  trailingVisual: TrailingVisual,
  trailingAction,
  label,
  items,
  displayCount = false,
  appearance,
  isLoading: isLoadingProp = false,
  isDisabled,
  isClearable,
  loaderPosition = 'trailing',
  ...props
}) => {
  const inputRef = useRef(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  // local state to synchronize with the isLoading prop
  const localLoadingState = isLoadingProp;
  const [isLoading, setIsLoading] = useState(false);
  // Sync internal loading state with prop
  useEffect(() => {
    setIsLoading(localLoadingState);
  }, [localLoadingState]);
  const showTrailingLoadingIndicator = isLoading && loaderPosition === 'trailing';
  const handleInputFocus = () => {
    setIsInputFocused(true);
  };
  const handleInputBlur = () => {
    if (!isInputFocused) {
      setIsInputFocused(true);
    } else {
      setIsInputFocused(false);
    }
  };

  // iterables aren't mappable
  const copiedItems = [...items];
  const {
    startsWith
  } = useFilter({
    sensitivity: 'base'
  });
  const [filterValue, setFilterValue] = React__default.useState('');
  React__default.useMemo(() => copiedItems.filter(item => startsWith(item.name, filterValue)), [items, filterValue]);

  // Store input value, selected option, open state, and items
  // in a state tracker
  React__default.useState({
    selectedKey: '',
    inputValue: '',
    items: copiedItems
  });
  return /*#__PURE__*/React__default.createElement(StyledSelect, {
    label: label,
    defaultItems: copiedItems,
    inputValue: filterValue,
    onInputChange: setFilterValue,
    allowsCustomValue: true,
    menuTrigger: "focus",
    defaultInputValue: "Make a selection"
  }, /*#__PURE__*/React__default.createElement(StyledSelectLabel, null, label), /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      position: 'relative'
    }
  }, /*#__PURE__*/React__default.createElement(StyledSelectInputWrapper, {
    appearance: appearance,
    isInputFocused: isInputFocused,
    isDisabled: isDisabled
  }, /*#__PURE__*/React__default.createElement(UnstyledSelectInput, {
    placeholder: placeholder,
    ref: inputRef,
    disabled: isDisabled,
    onFocus: handleInputFocus,
    onBlur: handleInputBlur
  }), /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      position: 'absolute',
      right: 0,
      top: 0,
      p: 2
    }
  }, (showTrailingLoadingIndicator || TrailingVisual) && /*#__PURE__*/React__default.createElement(InnerVisualSlot, {
    visualPosition: "trailing",
    showLoadingIndicator: showTrailingLoadingIndicator,
    hasLoadingIndicator: typeof isLoading === 'boolean'
  }, /*#__PURE__*/React__default.createElement(StyledSelectTrigger, null, TrailingVisual && typeof TrailingVisual === 'function' ? /*#__PURE__*/React__default.createElement(TrailingVisual, null) : TrailingVisual || /*#__PURE__*/React__default.createElement(AkChevronDownIcon, null)))), /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      position: 'absolute',
      right: '30px',
      top: '2px',
      p: 2
    }
  }, isClearable ? /*#__PURE__*/React__default.createElement(SelectClearButton, null) : trailingAction || null))), description && /*#__PURE__*/React__default.createElement(StyledSelectText, {
    slot: "description"
  }, description), errorMessage && /*#__PURE__*/React__default.createElement(StyledSelectText, {
    slot: "errorMessage"
  }, errorMessage), /*#__PURE__*/React__default.createElement(StyledSelectPopover, null, /*#__PURE__*/React__default.createElement(StyledSelectListbox, null, item => /*#__PURE__*/React__default.createElement(AtlasSelectItem, {
    textValue: item.displayName,
    label: item.name,
    leadingVisual: item.icon
  }, /*#__PURE__*/React__default.createElement(Stack, {
    size: 1
  }, /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      gap: 1
    }
  }, /*#__PURE__*/React__default.createElement(Text, {
    sx: {
      fontWeight: 'medium'
    }
  }, item.displayName), displayCount && /*#__PURE__*/React__default.createElement(Text, null, "(", item.count, ")")), /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      gap: 1
    }
  }, /*#__PURE__*/React__default.createElement(Text, {
    sx: {
      fontSize: 2,
      color: 'text.subtle'
    }
  }, item.description)))))));
};
AtlasComboboxSelect.displayName = "AtlasComboboxSelect";

export { AtlasComboboxSelect };
